import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { toCurrencyString } from '@delmonicos/shared-sdk/libs/core/src/utils/formatters/currency.formatters';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';
import * as css from '@app/feature/emsp-bo-rfid-badge-order/emsp-bo-rfid-badge-order-details/emsp-bo-rfid-badge-order-details.module.scss';
import { mapEmspBoRfidBadgeOrderStatusToStyle } from '@app/feature/emsp-bo-rfid-badge-order/emsp-bo-rfid-badge-order-status.helper';
import { useEmspBoRfidBadgeOrderContext } from '@app/feature/emsp-bo-rfid-badge-order/emsp-bo-rfid-badge-order.context';

export const GeneralCardComponent = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const order = useEmspBoRfidBadgeOrderContext();

  // RENDER ////////////////////////////////////////////////////////////////////

  const { color: statusColor } = mapEmspBoRfidBadgeOrderStatusToStyle(
    order.status,
  );

  return (
    <Card
      className={css.generalCard}
      direction="column"
      gap={GapEnum.M}
      flex={1}>
      <AccentTitle
        icon="info-circle"
        as={HeadingEnum.H2}
        text={t('$.emsp-bo-rfid-badge-order-details.information.title')}
      />
      <Stack gap={GapEnum.S} justifyContent="space-between" alignItems="center">
        <Stack flex={1} alignItems="center">
          <Text
            color={TextColor.Secondary}
            uppercase
            fontSize={TextFontSize.Small}>
            {t('delmonicos-shared-sdk.outputs-rfid-badge-order.order-number')}
          </Text>
        </Stack>
        <Stack flex={1} alignItems="center">
          <Text>{order.orderNumber}</Text>
        </Stack>
      </Stack>
      <Stack gap={GapEnum.S} justifyContent="space-between" alignItems="center">
        <Stack flex={1} alignItems="center">
          <Text
            color={TextColor.Secondary}
            uppercase
            fontSize={TextFontSize.Small}>
            {t('delmonicos-shared-sdk.outputs-rfid-badge-order.status')}
          </Text>
        </Stack>
        <Stack flex={1} alignItems="center">
          <Tag className={classNames(statusColor)}>
            <Text italic fontSize={TextFontSize.Small}>
              {t(
                `delmonicos-shared-sdk.enums-rfid-badge-order-status.${order.status}`,
              )}
            </Text>
          </Tag>
        </Stack>
      </Stack>
      <Stack gap={GapEnum.S} justifyContent="space-between" alignItems="center">
        <Stack flex={1} alignItems="center">
          <Text
            color={TextColor.Secondary}
            uppercase
            fontSize={TextFontSize.Small}>
            {t('delmonicos-shared-sdk.outputs-rfid-badge-order.quantity')}
          </Text>
        </Stack>
        <Stack flex={1} alignItems="center">
          <Text>{order.quantity}</Text>
        </Stack>
      </Stack>
      <Stack gap={GapEnum.S} justifyContent="space-between" alignItems="center">
        <Stack flex={1} alignItems="center">
          <Text
            color={TextColor.Secondary}
            uppercase
            fontSize={TextFontSize.Small}>
            {t('delmonicos-shared-sdk.outputs-rfid-badge-order.unit-price-ht')}
          </Text>
        </Stack>
        <Stack flex={1} alignItems="center">
          <Text>{toCurrencyString(order.unitPriceHT)}</Text>
        </Stack>
      </Stack>
      <Stack gap={GapEnum.S} justifyContent="space-between" alignItems="center">
        <Stack flex={1} alignItems="center">
          <Text
            color={TextColor.Secondary}
            uppercase
            fontSize={TextFontSize.Small}>
            {t(
              'delmonicos-shared-sdk.outputs-rfid-badge-order.shipping-price-ht',
            )}
          </Text>
        </Stack>
        <Stack flex={1} alignItems="center">
          <Text>{toCurrencyString(order.shippingPriceHT)}</Text>
        </Stack>
      </Stack>
      {order.totalTTC !== undefined && (
        <Stack
          gap={GapEnum.S}
          justifyContent="space-between"
          alignItems="center">
          <Stack flex={1} alignItems="center">
            <Text
              color={TextColor.Secondary}
              uppercase
              fontSize={TextFontSize.Small}>
              {t('delmonicos-shared-sdk.outputs-rfid-badge-order.total-ttc')}
            </Text>
          </Stack>
          <Stack flex={1} alignItems="center">
            <Text>{toCurrencyString(order.totalTTC)}</Text>
          </Stack>
        </Stack>
      )}
      <Divider />
      {order.confirmedAt && (
        <Stack
          gap={GapEnum.S}
          justifyContent="space-between"
          alignItems="center">
          <Stack flex={1} alignItems="center">
            <Text
              color={TextColor.Secondary}
              uppercase
              fontSize={TextFontSize.Small}>
              {t('delmonicos-shared-sdk.outputs-rfid-badge-order.confirmed-at')}
            </Text>
          </Stack>
          <Stack flex={1} alignItems="center">
            <Text>{formatDate(order.confirmedAt, i18n)}</Text>
          </Stack>
        </Stack>
      )}
      {order.paidAt && (
        <Stack
          gap={GapEnum.S}
          justifyContent="space-between"
          alignItems="center">
          <Stack flex={1} alignItems="center">
            <Text
              color={TextColor.Secondary}
              uppercase
              fontSize={TextFontSize.Small}>
              {t('delmonicos-shared-sdk.outputs-rfid-badge-order.paid-at')}
            </Text>
          </Stack>
          <Stack flex={1} alignItems="center">
            <Text>{formatDate(order.paidAt, i18n)}</Text>
          </Stack>
        </Stack>
      )}
      {order.shippedAt && (
        <Stack
          gap={GapEnum.S}
          justifyContent="space-between"
          alignItems="center">
          <Stack flex={1} alignItems="center">
            <Text
              color={TextColor.Secondary}
              uppercase
              fontSize={TextFontSize.Small}>
              {t('delmonicos-shared-sdk.outputs-rfid-badge-order.shipped-at')}
            </Text>
          </Stack>
          <Stack flex={1} alignItems="center">
            <Text>{formatDate(order.shippedAt, i18n)}</Text>
          </Stack>
        </Stack>
      )}
      {order.completedAt && (
        <Stack
          gap={GapEnum.S}
          justifyContent="space-between"
          alignItems="center">
          <Stack flex={1} alignItems="center">
            <Text
              color={TextColor.Secondary}
              uppercase
              fontSize={TextFontSize.Small}>
              {t('delmonicos-shared-sdk.outputs-rfid-badge-order.completed-at')}
            </Text>
          </Stack>
          <Stack flex={1} alignItems="center">
            <Text>{formatDate(order.completedAt, i18n)}</Text>
          </Stack>
        </Stack>
      )}
      {order.cancelledAt && (
        <Stack
          gap={GapEnum.S}
          justifyContent="space-between"
          alignItems="center">
          <Stack flex={1} alignItems="center">
            <Text
              color={TextColor.Secondary}
              uppercase
              fontSize={TextFontSize.Small}>
              {t('delmonicos-shared-sdk.outputs-rfid-badge-order.cancelled-at')}
            </Text>
          </Stack>
          <Stack flex={1} alignItems="center">
            <Text>{formatDate(order.cancelledAt, i18n)}</Text>
          </Stack>
        </Stack>
      )}
      {order.cancellationReason && (
        <Stack
          gap={GapEnum.S}
          justifyContent="space-between"
          alignItems="center">
          <Stack flex={1} alignItems="center">
            <Text
              color={TextColor.Secondary}
              uppercase
              fontSize={TextFontSize.Small}>
              {t(
                'delmonicos-shared-sdk.outputs-rfid-badge-order.cancellation-reason',
              )}
            </Text>
          </Stack>
          <Stack flex={1} alignItems="center">
            <Text>{order.cancellationReason}</Text>
          </Stack>
        </Stack>
      )}
      {order.rfidBadgeVisualNumbers &&
        order.rfidBadgeVisualNumbers.length > 0 && (
          <>
            <Divider />
            <Stack
              gap={GapEnum.S}
              justifyContent="space-between"
              alignItems="center">
              <Stack flex={1} alignItems="center">
                <Text
                  color={TextColor.Secondary}
                  uppercase
                  fontSize={TextFontSize.Small}>
                  {t(
                    'delmonicos-shared-sdk.outputs-rfid-badge-order.rfid-badge-visual-numbers',
                  )}
                </Text>
              </Stack>
              <Stack flex={1} direction="column" gap={GapEnum.XS}>
                {order.rfidBadgeVisualNumbers.map((vn) => (
                  <Text key={vn}>{vn}</Text>
                ))}
              </Stack>
            </Stack>
          </>
        )}
    </Card>
  );
};
